import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { publicApi } from "@/lib/api";

/**
 * Page-scoped code injection for single-page navigations.
 *
 * The server renders every applicable script into the initial HTML. But a
 * visitor who lands on the homepage and then clicks through to /contact never
 * loads a new document, so a script scoped to the contact page alone would never
 * run for them.
 *
 * This component closes that gap. On each route change it asks which scripts
 * apply and injects any it has not already run.
 *
 * Two rules it will not break:
 *   - nothing is ever injected on an /admin path;
 *   - a script is executed at most once per page load, tracked by ID. Re-running
 *     an analytics tag on every navigation would double-count every session.
 */

export default function CodeInjections() {
  const location = useLocation();
  const executedRef = useRef(new Set());
  const nodesRef = useRef([]);

  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return undefined;

    let cancelled = false;

    (async () => {
      let injections;
      try {
        const response = await publicApi.codeInjections(location.pathname);
        injections = response.items || [];
      } catch (_) {
        // Tracking is not worth breaking the page over.
        return;
      }
      if (cancelled) return;

      injections.forEach((injection) => {
        if (executedRef.current.has(injection.id)) return;
        executedRef.current.add(injection.id);

        // The markup may contain several elements — a comment, an external
        // <script src>, and an inline block. Parsing it lets each be handled
        // correctly rather than assuming a single tag.
        const template = document.createElement("template");
        template.innerHTML = injection.code;

        const target =
          injection.location === "head" ? document.head : document.body;

        Array.from(template.content.childNodes).forEach((node) => {
          // Scripts inserted via innerHTML never execute. Recreating the element
          // and copying its attributes is what makes the browser run it.
          if (node.nodeName === "SCRIPT") {
            const script = document.createElement("script");
            Array.from(node.attributes).forEach((attr) =>
              script.setAttribute(attr.name, attr.value)
            );
            script.text = node.textContent || "";
            script.setAttribute("data-injection", injection.id);
            target.appendChild(script);
            nodesRef.current.push(script);
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            const clone = node.cloneNode(true);
            clone.setAttribute("data-injection", injection.id);
            target.appendChild(clone);
            nodesRef.current.push(clone);
          }
        });
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  return null;
}
